import Vue from 'vue';
import * as i18next from 'i18next';
import { debounce } from 'lodash';
import configurationDataApi from '@/api/configurationDataApi';
import editConfigurationDataApi from '@/api/editConfigurationDataApi';
import settingsApi from '@/api/settingsApi';
import configurationDataStringApi from '@/api/configurationDataStringApi';
import modelPortfolioDetailsApi from '@/api/modelPortfolioDetailsApi';
import modelPortfolioPositionsApi from '@/api/modelPortfolioPositionsApi';
import investmentDiagramApi from '@/api/investmentDiagramApi';
import cryptoRandomString from 'crypto-random-string';
import riskToleranceApi from '../api/riskToleranceApi';
import { Section, ScreenSource } from '@/enums';
import { InvestmentStrategyType, DetailsFilterType } from '@/api/enums';
import { hostRedirect } from '@/services/messageService';
import i18nApiService from '@/api/i18nApiService';
import i18n from '@/i18n';
import httpClient from '@/api/httpClient';

import MutationTypes from '@/store/mutation-types';

const applicationError = (message, details = null) => ({
    message,
    details,
});

const serverError = ({ commit }, error) => {
    const { headers, method, baseURL, url, data } = error.config;
    const requestDetails = { headers, method, baseURL, url, data };
    const errorMessage = applicationError(
        'Application error',
        `${error.toString()}

${JSON.stringify(requestDetails, null, 2)}
    `,
    );
    commit(MutationTypes.APPLICATION_ERROR, errorMessage);
};

const setSelectedLanguage = ({ commit }, language) => {
    i18next.changeLanguage(language);
    commit(MutationTypes.SET_LANGUAGE, language);
};

const setIframeParams = ({ commit, state }, iframeParams) => {
    commit(MutationTypes.SET_IFRAME_PARAMS, iframeParams);
};

const setViewport = ({ commit, state }, payload) => {
    commit(MutationTypes.SET_VIEWPORT, payload);
};

const setInvestmentObjective = ({ commit }, objective) => {
    commit(MutationTypes.SET_INVESTMENT_OBJECTIVE, objective);
};

const setInvestmentStrategySelected = ({ commit, state }, investmentStrategy) => {
    if (state.openSections.includes(Section.INVESTMENT_STRATEGY) &&
      state.investmentStrategySelected &&
      state.investmentStrategySelected.name !== investmentStrategy.name) {
        commit(MutationTypes.SET_PREVIOUS_INVESTMENT_STRATEGY_SELECTED, state.investmentStrategySelected);
    }

    if (state.investmentStrategySelected && state.investmentStrategySelected.name !== investmentStrategy.name) {
        commit(MutationTypes.SET_LAST_OPEN_SECTION, Section.INVESTMENT_STRATEGY);
    }
    commit(MutationTypes.SET_INVESTMENT_STRATEGY_SELECTED, investmentStrategy);
};

const resetPreviousInvestmentStrategySelected = ({ commit, state }, investmentStrategy) => {
    commit(MutationTypes.RESET_PREVIOUS_INVESTMENT_STRATEGY_SELECTED);
};

const setInvestmentStrategyRecommendedBasedOnInvestmentHorizon = ({ commit }, objective) => {
    commit(MutationTypes.SET_INVESTMENT_STRATEGY_RECOMMENDED_BASED_ON_INVESTMENT_HORIZON, objective);
};

const setInvestmentStrategyRecommendedBasedOnRiskTolerance = ({ commit }, objective) => {
    commit(MutationTypes.SET_INVESTMENT_STRATEGY_RECOMMENDED_BASED_ON_RISK_TOLERANCE, objective);
};

const setInvestmentTopic = ({ commit, state }, topic) => {
    commit(MutationTypes.SET_INVESTMENT_THEME, topic);
};

const setInvestmentRegion = ({ commit }, objective) => {
    commit(MutationTypes.SET_REGIONAL_FOCUS, objective);
};

const setOtherInvestments = ({ commit }, objective) => {
    commit(MutationTypes.SET_OTHER_INVESTMENTS, objective);
};

const setInvestmentAmount = ({ commit }, amount) => {
    commit(MutationTypes.SET_INVESTMENT_AMOUNT, amount);
};

const setInvestmentHorizon = ({ commit }, horizon) => {
    commit(MutationTypes.SET_INVESTMENT_HORIZON, horizon);
};

const setSelectedDetailsFilterType = ({ commit }, type) => {
    commit(MutationTypes.SET_DETAILS_FILTER_TYPE, type);
};

const setSelectedDetailsSubFilterType = ({ commit }, type) => {
    commit(MutationTypes.SET_DETAILS_SUB_FILTER_TYPE, type);
};

const setRiskToleranceBlock1SelectedAnswer = ({ commit }, type) => {
    commit(MutationTypes.SET_RISK_TOLERANCE_BLOCK_1_SELECTED_ANSWER, type);
};

const setRiskToleranceBlock2SelectedAnswer = ({ commit }, type) => {
    commit(MutationTypes.SET_RISK_TOLERANCE_BLOCK_2_SELECTED_ANSWER, type);
};

const setRiskToleranceBlock3SelectedAnswer = ({ commit }, type) => {
    commit(MutationTypes.SET_RISK_TOLERANCE_BLOCK_3_SELECTED_ANSWER, type);
};

const setLastOpenSection = ({ commit }, section) => {
    commit(MutationTypes.SET_LAST_OPEN_SECTION, section);
};

const setEmailAddress = ({ commit }, emailAddress) => {
    commit(MutationTypes.SET_EMAIL_ADDRESS, emailAddress);
};

const setUserIsCustomer = ({ commit }, userIsCustomer) => {
    commit(MutationTypes.SET_USER_IS_CUSTOMER, userIsCustomer);
};

const addOpenSection = ({ commit }, section) => {
    commit(MutationTypes.ADD_OPEN_SECTION, section);
};

const removeOpenSection = ({ commit }, section) => {
    commit(MutationTypes.REMOVE_OPEN_SECTION, section);
};

const fetchModelPortfolioDetails = debounce(
    ({ commit, state }) => modelPortfolioDetailsApi
        .search(state)
        .then(data => commit(MutationTypes.SET_MODEL_PORTFOLIO_DETAILS, data))
    , 100);

const fetchModelPortfolioPositions = debounce(
    ({ commit, state }) => modelPortfolioPositionsApi.fetch(state)
        .then(data => commit(MutationTypes.SET_MODEL_PORTFOLIO_POSITIONS, data))
    , 100);

const fetchInvestmentDiagramData = ({ commit, getters }) => {
    return investmentDiagramApi.fetch(getters.investmentStrategySelected, getters.investmentHorizon, getters.investmentAmount)
        .then(data => {
            commit(MutationTypes.SET_INVESTMENT_DIAGRAM_RESPONSE, data);
        });
};

const resetInvestmentDiagramData = ({ commit, getters, state }) => {
    const data = [];
    for (let i = 0; i <= state.investmentHorizon * 12; i++) {
        data.push({
            investmentStrategyType: null,
            month: i,
            expectationLow: getters.investmentAmount,
            expectationAverage: getters.investmentAmount,
            expectationHigh: getters.investmentAmount,
        });
    }
    commit(MutationTypes.SET_INVESTMENT_DIAGRAM_RESPONSE, data);
};

const generateOrGetBrowserSessionId = () => {
    if (!Vue.ls.get('browserSessionId')) {
        Vue.ls.set('browserSessionId', cryptoRandomString({ length: 20, type: 'url-safe' }));
    }

    return Vue.ls.get('browserSessionId');
};

const saveConfigurationData = ({ commit, getters }) => {
    const configurationData = getters.configurationData;
    configurationData.browserSessionId = generateOrGetBrowserSessionId();
    return configurationDataApi.save(configurationData);
};

const saveConfigurationDataAndSendEmail = ({ commit, getters }) => {
    const configurationData = getters.configurationData;
    configurationData.browserSessionId = generateOrGetBrowserSessionId();
    configurationData.email = getters.emailAddress;

    return configurationDataApi.saveAndSendEmail({
        investmentProposalRequest: configurationData,
        emailAddress: getters.emailAddress,
        language: getters.selectedLanguage.toUpperCase(),
    });
};

const saveEditConfigurationData = ({ getters }) => {
    const configurationData = getters.configurationData;
    configurationData.browserSessionId = generateOrGetBrowserSessionId();
    return editConfigurationDataApi.save(configurationData, getters.editConfiguration.editToken);
};

const setEditConfiguration = ({ commit }, payload) => {
    return commit(MutationTypes.SET_EDIT_CONFIGURATION, payload);
};

const configurationStringToObjectToState = ({ getters, dispatch }, configurationString) => {
    return configurationDataStringApi.configurationStringToConfigurationObject(configurationString)
        .then(editConfiguration => {
            dispatch('setEditConfiguration', editConfiguration);
        });
};

const rebuildForm = ({ dispatch, getters, commit }, values) => {
    commit(MutationTypes.SET_IS_IN_EDIT_MODE, true); // Set global editMode flag

    // Investment Goals section
    dispatch('setInvestmentObjective', values.objective);
    dispatch('setInvestmentHorizon', values.timeHorizon);
    dispatch('setInvestmentAmount', values.amount);

    // Investment Strategy section
    dispatch('addOpenSection', Section.INVESTMENT_STRATEGY);
    dispatch('setInvestmentStrategySelected', InvestmentStrategyType[values.strategySelected]);

    // Risk Tolerance Questions: If risk questions are relevant fill out the section
    if (values.strategySelected === InvestmentStrategyType.DYNAMIC.name ||
        values.strategySelected === InvestmentStrategyType.GROWTH.name ||
        values.strategySelected === InvestmentStrategyType.BALANCED.name) {
        dispatch('setRiskToleranceBlock1SelectedAnswer', values.riskToleranceAnswer1);
        dispatch('setRiskToleranceBlock2SelectedAnswer', values.riskToleranceAnswer2);
        dispatch('setRiskToleranceBlock3SelectedAnswer', values.riskToleranceAnswer3);
    }

    // Evaluate survey always
    dispatch('evaluateSurvey').then(() => {
        dispatch('setInvestmentStrategyRecommendedBasedOnRiskTolerance', InvestmentStrategyType[values.strategyByRiskTolerance]);
        dispatch('setInvestmentStrategyRecommendedBasedOnInvestmentHorizon', InvestmentStrategyType[values.strategyByInvestmentHorizon]);
    });

    // Theme section
    dispatch('addOpenSection', Section.THEME);

    // These two need to be set before setting the investmentTopic
    dispatch('setInvestmentRegion', values.region);
    dispatch('setOtherInvestments', values.otherInvestments);

    // Setting the filter type is needed, as the model portfolio API call needs it
    dispatch('setSelectedDetailsFilterType', DetailsFilterType.ASSET_CLASS);

    // Warning: changing investmentTopic depends on investmentRegion and otherIncestmen
    dispatch('setInvestmentTopic', values.topic);
};

const loadTranslations = ({ commit }, language) => i18nApiService.loadTranslations(language).then(({ data: translations }) => {
    i18n.i18next.addResourceBundle(language, 'default', translations, true);
    i18n.i18next.setDefaultNamespace('default');
    i18next.changeLanguage(language);
}).catch(error => {
    // eslint-disable-next-line no-console
    console.error('Error during loading of translations', error);
});

const handleEditMode = ({ getters, dispatch }) => {
    const configurationString = getters.editValueList; // Originated from the URL query string, synced to the state
    if (configurationString) {
        return httpClient.get('/auth-edit-mode')
            .then(() => {
                dispatch('configurationStringToObjectToState', configurationString)
                    .then(() => {
                        dispatch('rebuildForm', getters.editConfiguration);
                    });
            })
            .catch(err => serverError(err.message));
    }
};

const updateRiskToleranceWithSelectedStrategy = ({ state, dispatch }) => {
    if (state.investmentStrategyRecommendedBasedOnRiskTolerance === null) {
        dispatch('setInvestmentStrategyRecommendedBasedOnRiskTolerance', state.investmentStrategySelected);
    } else if (state.investmentStrategySelected.risk < state.investmentStrategyRecommendedBasedOnRiskTolerance.risk) {
        dispatch('setInvestmentStrategyRecommendedBasedOnRiskTolerance', state.investmentStrategySelected);
    };
};

const evaluateSurvey = ({ getters, dispatch }) => {
    return riskToleranceApi.fetchInvestmentStrategyRecommendedBasedOnRiskTolerance({
        block1SelectedAnswer: getters.riskToleranceBlock1SelectedAnswer,
        block2SelectedAnswer: getters.riskToleranceBlock2SelectedAnswer,
        block3SelectedAnswer: getters.riskToleranceBlock3SelectedAnswer,
        investmentStrategySelected: getters.investmentStrategySelected.name,
    }).then(investmentStrategyType => {
        dispatch('setInvestmentStrategyRecommendedBasedOnRiskTolerance', InvestmentStrategyType[investmentStrategyType]);
    });
};

const discardEditModeChanges = ({ getters, state }) => {
    hostRedirect(`${getters.settings.mobileAppUrl}?editToken=${getters.editConfiguration.editToken}&configChanged=0&lang=${state.language}`);
};

const saveEditModeChanges = ({ getters, state, dispatch }) => {
    dispatch('saveEditConfigurationData').then(() => {
        hostRedirect(`${getters.settings.mobileAppUrl}?editToken=${getters.editConfiguration.editToken}&configChanged=1&lang=${state.language}`);
    });
};

const fetchSettings = ({ commit }) => {
    settingsApi.getSettings()
        .then(settings => {
            commit(MutationTypes.SET_SETTINGS, settings);
        });
};

const setScreenSource = ({ dispatch, commit }, source) => {
    const isScreenSourceWebAppMobile = (source === ScreenSource.WEB_APP_MOBILE);

    commit(MutationTypes.SET_SCREEN_SOURCE, source);

    if (isScreenSourceWebAppMobile) {
        dispatch('setUserIsCustomer', true);
    }
};

export default {
    serverError,
    setSelectedLanguage,
    setIframeParams,
    setViewport,
    setInvestmentObjective,
    setInvestmentStrategySelected,
    resetPreviousInvestmentStrategySelected,
    setInvestmentStrategyRecommendedBasedOnInvestmentHorizon,
    setInvestmentStrategyRecommendedBasedOnRiskTolerance,
    setInvestmentTopic,
    setInvestmentRegion,
    setOtherInvestments,
    setInvestmentAmount,
    setInvestmentHorizon,
    setSelectedDetailsFilterType,
    setSelectedDetailsSubFilterType,
    setRiskToleranceBlock1SelectedAnswer,
    setRiskToleranceBlock2SelectedAnswer,
    setRiskToleranceBlock3SelectedAnswer,
    setLastOpenSection,
    setEmailAddress,
    setUserIsCustomer,
    addOpenSection,
    removeOpenSection,
    handleEditMode,
    fetchModelPortfolioDetails,
    fetchModelPortfolioPositions,
    fetchInvestmentDiagramData,
    resetInvestmentDiagramData,
    configurationStringToObjectToState,
    saveConfigurationData,
    saveConfigurationDataAndSendEmail,
    saveEditConfigurationData,
    setEditConfiguration,
    rebuildForm,
    updateRiskToleranceWithSelectedStrategy,
    evaluateSurvey,
    discardEditModeChanges,
    saveEditModeChanges,
    fetchSettings,
    loadTranslations,
    setScreenSource,
};
